/**
 * The incantation lexicon.
 *
 * Players may still type any chant they like — this is a suggestion list, not a
 * grammar. It exists because free invention fights the one part of the stack
 * nobody controls: speech recognition is a language model over a dictionary,
 * so an invented word comes back differently every time it is spoken and the
 * player is left blaming their own pronunciation. Every token here is a real
 * word an English recogniser already knows, which means a chant assembled from
 * them survives the round trip and still reads as an incantation.
 *
 * `common` marks the tokens a recogniser is most reliable on. The rest are
 * Latin flavour: they usually land close enough for `chantSimilarity`, but they
 * are the ones that will cost a player a cast on a bad mic, and the craft
 * screen says so.
 *
 * `connector` is the glue — the particles that turn three nouns into a sentence
 * you can shout. They also lengthen the chant, and chant length is what buys
 * effect budget, so they are never merely decorative.
 */

export const LEXICON_CATEGORIES = [
  'element',
  'force',
  'action',
  'ward',
  'doom',
  'domain',
  'epithet',
  'connector',
] as const;
export type LexiconCategory = (typeof LEXICON_CATEGORIES)[number];

export const LEXICON_CATEGORY_LABELS: Record<LexiconCategory, string> = {
  element: '원소',
  force: '권능',
  action: '행위',
  ward: '수호 · 반사',
  doom: '종언 · 죽음',
  domain: '영역 · 차원',
  epithet: '칭호 · 수식',
  connector: '연결어 (조사)',
};

export interface LexiconToken {
  /** Uppercase, ASCII only, so it survives `normalizeChant` unchanged. */
  word: string;
  /** Korean gloss shown next to the token in the forge. */
  ko: string;
  category: LexiconCategory;
  /** True when an English recogniser is reliable on this word. */
  common: boolean;
}

const t = (
  word: string,
  ko: string,
  category: LexiconCategory,
  common = true,
): LexiconToken => ({ word, ko, category, common });

export const SPELL_LEXICON: readonly LexiconToken[] = [
  /* --- element ----------------------------------------------------- */
  t('INFERNO', '화염지옥', 'element'),
  t('PYRE', '화장의 불', 'element'),
  t('EMBER', '잉걸불', 'element'),
  t('CINDER', '타다 남은 재', 'element'),
  t('MAGMA', '용암', 'element'),
  t('IGNIS', '불꽃', 'element', false),
  t('FROST', '서리', 'element'),
  t('RIME', '상고대', 'element'),
  t('GLACIER', '빙하', 'element'),
  t('GLACIES', '결빙', 'element', false),
  t('TEMPEST', '폭풍', 'element'),
  t('THUNDER', '뇌명', 'element'),
  t('LIGHTNING', '번개', 'element'),
  t('FULGUR', '뇌전', 'element', false),
  t('GALE', '질풍', 'element'),
  t('VENOM', '맹독', 'element'),
  t('TOXIN', '독소', 'element'),
  t('AQUA', '물', 'element'),
  t('TERRA', '대지', 'element'),
  t('AETHER', '에테르', 'element'),
  t('PLASMA', '플라스마', 'element'),
  t('OBSIDIAN', '흑요석', 'element'),
  t('BLAZE', '작열', 'element'),
  t('BRIMSTONE', '유황불', 'element'),
  t('WILDFIRE', '들불', 'element'),
  t('ASH', '잿가루', 'element'),
  t('FLAMMA', '불길', 'element', false),
  t('BLIZZARD', '눈보라', 'element'),
  t('PERMAFROST', '영구동토', 'element'),
  t('HAIL', '우박', 'element'),
  t('GELU', '혹한', 'element', false),
  t('CYCLONE', '선풍', 'element'),
  t('TYPHOON', '태풍', 'element'),
  t('TORRENT', '급류', 'element'),
  t('TIDE', '조수', 'element'),
  t('UNDA', '물결', 'element', false),
  t('FULMEN', '벼락', 'element', false),
  t('VENTUS', '바람', 'element', false),
  t('QUAKE', '지진', 'element'),
  t('TREMOR', '진동', 'element'),
  t('MIASMA', '장기(瘴氣)', 'element'),
  t('BLIGHT', '역병', 'element'),
  t('SPORE', '포자', 'element'),
  t('QUICKSILVER', '수은빛', 'element'),
  t('CRYSTAL', '수정', 'element'),
  t('AMBER', '호박(琥珀)', 'element'),
  t('STARLIGHT', '별빛', 'element'),
  t('MOONLIGHT', '달빛', 'element'),
  t('SUNFIRE', '태양불', 'element'),

  /* --- force ------------------------------------------------------- */
  t('MANA', '마나', 'force'),
  t('ARCANE', '비의(祕儀)', 'force'),
  t('ARCANUM', '비오(祕奧)', 'force', false),
  t('SHADOW', '그림자', 'force'),
  t('UMBRA', '그늘', 'force', false),
  t('TENEBRIS', '어둠의', 'force', false),
  t('RADIANCE', '광채', 'force'),
  t('LUMEN', '광량', 'force', false),
  t('SANGUINE', '핏빛', 'force'),
  t('SANGUIS', '피', 'force', false),
  t('ANIMA', '혼(魂)', 'force'),
  t('SPIRIT', '영(靈)', 'force'),
  t('CHAOS', '혼돈', 'force'),
  t('ENTROPY', '엔트로피', 'force'),
  t('GRAVITY', '중력', 'force'),
  t('SILENCE', '정적', 'force'),
  t('ECHO', '메아리', 'force'),
  t('FURY', '격노', 'force'),
  t('WRATH', '진노', 'force'),
  t('MALICE', '악의', 'force'),
  t('DESPAIR', '절망', 'force'),
  t('HATRED', '증오', 'force'),
  t('VENGEANCE', '복수', 'force'),
  t('SORROW', '비애', 'force'),
  t('AGONY', '고통', 'force'),
  t('TORMENT', '고뇌', 'force'),
  t('MADNESS', '광기', 'force'),
  t('FRENZY', '광란', 'force'),
  t('HUNGER', '굶주림', 'force'),
  t('WILL', '의지', 'force'),
  t('RESOLVE', '결의', 'force'),
  t('COVENANT', '맹약', 'force'),
  t('OATH', '서약', 'force'),
  t('CURSE', '저주', 'force'),
  t('HEX', '주술', 'force'),
  t('OMEN', '전조', 'force'),
  t('KARMA', '업(業)', 'force'),
  t('FATE', '운명', 'force'),
  t('DESTINY', '숙명', 'force'),
  t('PSYCHE', '정신', 'force'),
  t('FLUX', '유동', 'force'),
  t('INERTIA', '관성', 'force'),
  t('RESONANCE', '공명', 'force'),
  t('DISSONANCE', '불협화', 'force'),
  t('VOX', '목소리', 'force', false),
  t('VIS', '힘', 'force', false),
  t('IRA', '분노', 'force', false),
  t('LUX', '빛', 'force', false),
  t('NOX', '밤', 'force', false),
  t('ODIUM', '증오', 'force', false),

  /* --- action ------------------------------------------------------ */
  t('IGNITE', '점화하다', 'action'),
  t('IMMOLATE', '불살라 바치다', 'action'),
  t('SHATTER', '분쇄하다', 'action'),
  t('SUNDER', '갈라놓다', 'action'),
  t('REND', '찢다', 'action'),
  t('PIERCE', '관통하다', 'action'),
  t('IMPALE', '꿰뚫다', 'action'),
  t('DEVOUR', '포식하다', 'action'),
  t('CONSUME', '집어삼키다', 'action'),
  t('BANISH', '추방하다', 'action'),
  t('BIND', '속박하다', 'action'),
  t('SEAL', '봉인하다', 'action'),
  t('UNSEAL', '봉인을 풀다', 'action'),
  t('SUMMON', '소환하다', 'action'),
  t('INVOKE', '발현하다', 'action'),
  t('AWAKEN', '각성하다', 'action'),
  t('ASCEND', '승천하다', 'action'),
  t('DESCEND', '강림하다', 'action'),
  t('COLLAPSE', '붕괴시키다', 'action'),
  t('ERUPT', '분출하다', 'action'),
  t('FRACTURE', '균열내다', 'action'),
  t('ANNIHILATE', '절멸시키다', 'action'),
  t('OBLITERATE', '소멸시키다', 'action'),
  t('PURGE', '정화하다', 'action'),
  t('CORRUPT', '오염시키다', 'action'),
  t('HASTEN', '가속하다', 'action'),
  t('WITHER', '시들게 하다', 'action'),
  t('PETRIFY', '석화시키다', 'action'),
  t('CLEAVE', '베어 가르다', 'action'),
  t('SEVER', '절단하다', 'action'),
  t('CRUSH', '으스러뜨리다', 'action'),
  t('SMITE', '내리치다', 'action'),
  t('LACERATE', '난도질하다', 'action'),
  t('ENGULF', '집어삼키다', 'action'),
  t('ENSHROUD', '뒤덮다', 'action'),
  t('ENTOMB', '매장하다', 'action'),
  t('ENSNARE', '옭아매다', 'action'),
  t('SHACKLE', '결박하다', 'action'),
  t('BLIND', '눈멀게 하다', 'action'),
  t('DEFILE', '더럽히다', 'action'),
  t('DESECRATE', '모독하다', 'action'),
  t('SANCTIFY', '성별(聖別)하다', 'action'),
  t('CLEANSE', '씻어내다', 'action'),
  t('RESTORE', '회복시키다', 'action'),
  t('MEND', '치유하다', 'action'),
  t('SCATTER', '흩뿌리다', 'action'),
  t('SPLINTER', '쪼개다', 'action'),
  t('VANISH', '사라지다', 'action'),
  t('BLINK', '점멸하다', 'action'),
  t('WARP', '왜곡시키다', 'action'),
  t('REWIND', '되감다', 'action'),
  t('SUPPRESS', '억누르다', 'action'),
  t('DOMINATE', '지배하다', 'action'),
  t('SUBJUGATE', '복종시키다', 'action'),
  t('DEVASTATE', '초토화하다', 'action'),
  t('RAVAGE', '유린하다', 'action'),
  t('UNLEASH', '해방하다', 'action'),
  t('MANIFEST', '현현하다', 'action'),
  t('INCARNATE', '육화(肉化)하다', 'action'),
  t('TRANSCEND', '초월하다', 'action'),
  t('DECREE', '선포하다', 'action'),

  /* --- ward -------------------------------------------------------- */
  t('AEGIS', '신위의 방패', 'ward'),
  t('BULWARK', '방벽', 'ward'),
  t('BASTION', '보루', 'ward'),
  t('SANCTUM', '성역', 'ward'),
  t('SANCTUARY', '성소', 'ward'),
  t('WARD', '결계', 'ward'),
  t('BARRIER', '장벽', 'ward'),
  t('MIRROR', '거울', 'ward'),
  t('REFLECT', '반사하다', 'ward'),
  t('REVERSE', '역전시키다', 'ward'),
  t('ABSORB', '흡수하다', 'ward'),
  t('NEGATE', '무효화하다', 'ward'),
  t('NULLIFY', '무로 되돌리다', 'ward'),
  t('DEFLECT', '흘려내다', 'ward'),
  t('INVIOLATE', '침범할 수 없는', 'ward'),
  t('ADAMANT', '금강(金剛)', 'ward'),
  t('TUTELA', '가호', 'ward', false),
  t('RAMPART', '성벽', 'ward'),
  t('CITADEL', '아성', 'ward'),
  t('FORTRESS', '요새', 'ward'),
  t('REFUGE', '피난처', 'ward'),
  t('VEIL', '장막', 'ward'),
  t('SHROUD', '수의(壽衣)', 'ward'),
  t('MANTLE', '외피', 'ward'),
  t('CARAPACE', '갑각', 'ward'),
  t('PHALANX', '방진(方陣)', 'ward'),
  t('PARRY', '받아넘기다', 'ward'),
  t('REBUKE', '물리치다', 'ward'),
  t('REPEL', '격퇴하다', 'ward'),
  t('REBOUND', '되튀기다', 'ward'),
  t('REDIRECT', '되돌리다', 'ward'),
  t('SIPHON', '빨아들이다', 'ward'),
  t('IMMUNE', '불침(不侵)의', 'ward'),
  t('UNBREAKABLE', '불괴(不壞)의', 'ward'),
  t('SCUTUM', '방패', 'ward', false),
  t('MURUS', '성벽', 'ward', false),
  t('SPECULUM', '거울', 'ward', false),

  /* --- doom -------------------------------------------------------- */
  t('DOOM', '파멸', 'doom'),
  t('NEMESIS', '응보', 'doom'),
  t('REQUIEM', '진혼(鎭魂)', 'doom'),
  t('ELEGY', '만가(輓歌)', 'doom'),
  t('DIRGE', '장송곡', 'doom'),
  t('OBLIVION', '망각', 'doom'),
  t('THANATOS', '타나토스', 'doom'),
  t('RAGNAROK', '라그나로크', 'doom'),
  t('APOCALYPSE', '종말', 'doom'),
  t('CATACLYSM', '대재앙', 'doom'),
  t('EXTINCTION', '절멸', 'doom'),
  t('EXECUTION', '처형', 'doom'),
  t('VERDICT', '판결', 'doom'),
  t('JUDGMENT', '심판', 'doom'),
  t('TERMINUS', '종착', 'doom'),
  t('FINALE', '종막', 'doom'),
  t('OMEGA', '오메가', 'doom'),
  t('MORTEM', '죽음을', 'doom', false),
  t('EXITIUM', '파멸', 'doom', false),
  t('ARMAGEDDON', '아마겟돈', 'doom'),
  t('REVELATION', '묵시', 'doom'),
  t('REAPER', '사신', 'doom'),
  t('GRAVE', '무덤', 'doom'),
  t('TOMB', '묘소', 'doom'),
  t('SEPULCHER', '석관묘', 'doom'),
  t('EPITAPH', '묘비명', 'doom'),
  t('LAMENT', '비가(悲歌)', 'doom'),
  t('KNELL', '조종(弔鐘)', 'doom'),
  t('DOWNFALL', '몰락', 'doom'),
  t('RUIN', '폐허', 'doom'),
  t('DECAY', '쇠락', 'doom'),
  t('TRIBUNAL', '심판정', 'doom'),
  t('INQUISITION', '심문', 'doom'),
  t('PENANCE', '속죄', 'doom'),
  t('RETRIBUTION', '응징', 'doom'),
  t('FINIS', '끝', 'doom', false),
  t('MORS', '죽음', 'doom', false),
  t('FATUM', '운명', 'doom', false),
  t('INTERITUS', '멸망', 'doom', false),

  /* --- domain ------------------------------------------------------ */
  t('ABYSS', '심연', 'domain'),
  t('VOID', '공허', 'domain'),
  t('CHASM', '나락', 'domain'),
  t('LABYRINTH', '미궁', 'domain'),
  t('VORTEX', '소용돌이', 'domain'),
  t('SINGULARITY', '특이점', 'domain'),
  t('HORIZON', '지평', 'domain'),
  t('ZENITH', '천정(天頂)', 'domain'),
  t('NADIR', '천저(天底)', 'domain'),
  t('ECLIPSE', '식(蝕)', 'domain'),
  t('GENESIS', '창세', 'domain'),
  t('CRADLE', '요람', 'domain'),
  t('THRONE', '옥좌', 'domain'),
  t('CATHEDRAL', '대성당', 'domain'),
  t('NECROPOLIS', '사자의 도시', 'domain'),
  t('PANDEMONIUM', '수라장', 'domain'),
  t('TARTARUS', '타르타로스', 'domain'),
  t('LIMBO', '림보', 'domain'),
  t('MAELSTROM', '대소용돌이', 'domain'),
  t('RIFT', '균열', 'domain'),
  t('BREACH', '틈', 'domain'),
  t('GATE', '문(門)', 'domain'),
  t('THRESHOLD', '문턱', 'domain'),
  t('CRYPT', '지하 묘소', 'domain'),
  t('CATACOMB', '카타콤', 'domain'),
  t('OSSUARY', '납골당', 'domain'),
  t('SPIRE', '첨탑', 'domain'),
  t('OBELISK', '오벨리스크', 'domain'),
  t('MONOLITH', '거석', 'domain'),
  t('ALTAR', '제단', 'domain'),
  t('FIRMAMENT', '창궁', 'domain'),
  t('COSMOS', '우주', 'domain'),
  t('NEBULA', '성운', 'domain'),
  t('NOVA', '신성(新星)', 'domain'),
  t('CONSTELLATION', '성좌', 'domain'),
  t('ELYSIUM', '엘리시움', 'domain'),
  t('VALHALLA', '발할라', 'domain'),
  t('STYX', '스틱스', 'domain'),
  t('ACHERON', '아케론', 'domain'),
  t('NEXUS', '결절점', 'domain'),
  t('AXIS', '축(軸)', 'domain'),
  t('COCYTUS', '코퀴토스', 'domain', false),
  t('AVERNUS', '아베르누스', 'domain', false),
  t('CAELUM', '천계', 'domain', false),

  /* --- epithet ----------------------------------------------------- */
  t('ETERNAL', '영원한', 'epithet'),
  t('AETERNUM', '영겁의', 'epithet', false),
  t('INFINITE', '무한한', 'epithet'),
  t('ABSOLUTE', '절대의', 'epithet'),
  t('PRIMORDIAL', '태초의', 'epithet'),
  t('ANCIENT', '태고의', 'epithet'),
  t('FORBIDDEN', '금단의', 'epithet'),
  t('FORSAKEN', '버림받은', 'epithet'),
  t('NAMELESS', '이름 없는', 'epithet'),
  t('CRIMSON', '진홍의', 'epithet'),
  t('AZURE', '창천의', 'epithet'),
  t('ONYX', '칠흑의', 'epithet'),
  t('PALE', '창백한', 'epithet'),
  t('HOLLOW', '공허한', 'epithet'),
  t('SOVEREIGN', '지고의', 'epithet'),
  t('TYRANT', '폭군', 'epithet'),
  t('HERALD', '전령', 'epithet'),
  t('ORACLE', '신탁', 'epithet'),
  t('PHANTOM', '환영', 'epithet'),
  t('SPECTER', '망령', 'epithet'),
  t('SERAPH', '치천사', 'epithet'),
  t('BEHEMOTH', '거수(巨獸)', 'epithet'),
  t('LEVIATHAN', '리바이어던', 'epithet'),
  t('WYRM', '고룡(古龍)', 'epithet'),
  t('ULTIMATE', '궁극의', 'epithet'),
  t('SUPREME', '지고의', 'epithet'),
  t('DIVINE', '신성한', 'epithet'),
  t('PROFANE', '불경한', 'epithet'),
  t('UNHOLY', '부정(不淨)한', 'epithet'),
  t('SACRED', '거룩한', 'epithet'),
  t('CURSED', '저주받은', 'epithet'),
  t('DAMNED', '단죄받은', 'epithet'),
  t('FALLEN', '타락한', 'epithet'),
  t('EXILED', '추방된', 'epithet'),
  t('SILENT', '침묵의', 'epithet'),
  t('FROZEN', '얼어붙은', 'epithet'),
  t('BURNING', '불타는', 'epithet'),
  t('WITHERED', '메마른', 'epithet'),
  t('SHATTERED', '부서진', 'epithet'),
  t('BOUNDLESS', '가없는', 'epithet'),
  t('NIGHTMARE', '악몽의', 'epithet'),
  t('DREAD', '공포의', 'epithet'),
  t('GRIM', '음산한', 'epithet'),
  t('SABLE', '흑담비빛', 'epithet'),
  t('VERMILION', '주홍의', 'epithet'),
  t('VIRIDIAN', '청록의', 'epithet'),
  t('GOLDEN', '황금의', 'epithet'),
  t('SILVER', '은빛의', 'epithet'),
  t('SCARLET', '선혈빛', 'epithet'),
  t('CHIMERA', '키마이라', 'epithet'),
  t('BASILISK', '바실리스크', 'epithet'),
  t('WYVERN', '와이번', 'epithet'),
  t('KRAKEN', '크라켄', 'epithet'),
  t('TITAN', '거신(巨神)', 'epithet'),
  t('COLOSSUS', '거상(巨像)', 'epithet'),
  t('ARCHON', '아르콘', 'epithet'),
  t('MONARCH', '군주', 'epithet'),
  t('EMPEROR', '황제', 'epithet'),
  t('WARDEN', '수문장', 'epithet'),
  t('HARBINGER', '전조자', 'epithet'),
  t('REVENANT', '망자(亡者)', 'epithet'),
  t('WRAITH', '원귀', 'epithet'),
  t('LICH', '리치', 'epithet'),
  t('SERPENT', '뱀', 'epithet'),
  t('DRAGON', '용(龍)', 'epithet'),
  t('ULTIMA', '최후의', 'epithet', false),

  /* --- connector --------------------------------------------------- */
  t('OF', '~의', 'connector'),
  t('THE', '(정관사)', 'connector'),
  t('AND', '그리고', 'connector'),
  t('FROM', '~로부터', 'connector'),
  t('BEYOND', '~너머의', 'connector'),
  t('WITHIN', '~안에서', 'connector'),
  t('AGAINST', '~에 맞서', 'connector'),
  t('BENEATH', '~아래에', 'connector'),
  t('UPON', '~위에', 'connector'),
  t('UNTO', '~에게로', 'connector'),
  t('WITHOUT', '~없이', 'connector'),
  t('THROUGH', '~을 통하여', 'connector'),
  t('VIA', '~을 거쳐', 'connector'),
  t('PER', '~을 통해', 'connector'),
  t('ULTRA', '~을 넘어', 'connector'),
  t('CONTRA', '~에 항거하여', 'connector'),
  t('SUPRA', '~위에', 'connector', false),
  t('INTER', '~사이에', 'connector', false),
  t('TRANS', '~을 건너', 'connector', false),
  t('EX', '~로부터', 'connector', false),
  t('AD', '~에게', 'connector', false),
  t('ET', '그리고', 'connector', false),
  t('SINE', '~없이', 'connector', false),
  t('CUM', '~와 함께', 'connector', false),
  t('INTO', '~안으로', 'connector'),
  t('ONTO', '~위로', 'connector'),
  t('OVER', '~너머로', 'connector'),
  t('UNDER', '~아래로', 'connector'),
  t('ABOVE', '~위의', 'connector'),
  t('BELOW', '~아래의', 'connector'),
  t('BEFORE', '~앞에', 'connector'),
  t('AFTER', '~뒤에', 'connector'),
  t('BETWEEN', '~사이의', 'connector'),
  t('AMONG', '~가운데', 'connector'),
  t('AMIDST', '~한복판에', 'connector'),
  t('BESIDE', '~곁에', 'connector'),
  t('BEHIND', '~뒤편에', 'connector'),
  t('TOWARD', '~을 향하여', 'connector'),
  t('ACROSS', '~을 가로질러', 'connector'),
  t('WITH', '~와 함께', 'connector'),
  t('FOR', '~를 위하여', 'connector'),
  t('IN', '~안의', 'connector'),
  t('ALL', '모든', 'connector'),
  t('MY', '나의', 'connector'),
  t('WHO', '~하는 자', 'connector'),
  t('THUS', '이리하여', 'connector'),
  t('HENCE', '그러므로', 'connector'),
  t('NOW', '지금', 'connector'),
  t('FOREVER', '영원히', 'connector'),
  t('NEVER', '결코', 'connector'),
  // Imperatives. Grammatically they are verbs, but in a chant they do a
  // connector's job — they open the sentence and hand it to whatever follows.
  t('LET', '~하게 하라', 'connector'),
  t('ARISE', '일어나라', 'connector'),
  t('BEHOLD', '보라', 'connector'),
  t('AWAIT', '기다리라', 'connector'),
  t('SUPER', '~위에', 'connector'),
  t('POST', '~이후에', 'connector'),
  t('THY', '그대의', 'connector', false),
  t('THINE', '그대의 것', 'connector', false),
  t('THOU', '그대여', 'connector', false),
  t('SUB', '~아래', 'connector', false),
  t('PRO', '~을 위하여', 'connector', false),
  t('ANTE', '~이전에', 'connector', false),
  t('CIRCA', '~무렵', 'connector', false),
  t('ERGO', '그러므로', 'connector', false),
  t('DE', '~로부터', 'connector', false),
];

/** Tokens of one category, in listed order. */
export function lexiconByCategory(category: LexiconCategory): LexiconToken[] {
  return SPELL_LEXICON.filter((token) => token.category === category);
}

/** Look a token up by its word, case-insensitively. */
export function findLexiconToken(word: string): LexiconToken | undefined {
  const upper = word.trim().toUpperCase();
  return SPELL_LEXICON.find((token) => token.word === upper);
}

/**
 * Sample incantations, used as rotating placeholder text in the forge. Each one
 * is assembled purely from the lexicon, so they double as a demonstration of
 * how the connectors are meant to be used.
 */
export const LEXICON_EXAMPLES: readonly string[] = [
  'AEGIS OF THE NAMELESS THRONE',
  'CRIMSON REQUIEM UNTO OBLIVION',
  'PIERCE THE ETERNAL BULWARK',
  'MIRROR OF THE HOLLOW ABYSS',
  'INFERNO BEYOND THE PALE HORIZON',
  'ABSOLUTE VERDICT OF THE ANCIENT WYRM',
  'BEHOLD THE FALLEN MONARCH OF ASH',
  'ARISE FROM THE FROZEN CRYPT',
  'LET THE GRIM TIDE DEVOUR ALL',
  'SCARLET OATH AGAINST MY NAMELESS DREAD',
  'BRIMSTONE WRATH UPON THE SILENT SPIRE',
  'SEVER THE COVENANT BENEATH VALHALLA',
];
